/**
 * ================================================================================
 * ARQUIVO: App.jsx
 * PROPÓSITO: Router principal + Layout Shell da SPA
 * ================================================================================
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { FinanceProvider, useFinance } from './context/FinanceContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import BottomNav from './components/layout/BottomNav';
import FAB from './components/layout/FAB';

import Login from './pages/Login';
import Register from './pages/Register';
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import SuperAdminUsers from './pages/SuperAdmin/Users';
import Dashboard from './pages/Dashboard';
import Wallets from './pages/Wallets';
import WalletDetail from './pages/WalletDetail';
import Transactions from './pages/Transactions';
import TransactionForm from './pages/TransactionForm';
import Categories from './pages/Categories';
import Budgets from './pages/Budgets';
import Goals from './pages/Goals';
import GoalDetail from './pages/GoalDetail';
import RecurringTransactions from './pages/RecurringTransactions';
import Reports from './pages/Reports';
import Settings from './pages/Settings';

// ===== LAYOUT AUTENTICADO =====
function AuthLayout() {
  const { mobileLayout, mobileLayouts } = useTheme();
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="app-main">
        <Header />
        <main className="app-content">
          <Outlet />
        </main>
      </div>
      {mobileLayout === mobileLayouts.BOTTOM_NAV && <BottomNav />}
      <FAB />
    </div>
  );
}

// ===== ROTA PROTEGIDA =====
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useFinance();

  if (loading) {
    return (
      <div className="login-container">
        <div className="sidebar-brand-logo" style={{ width: 56, height: 56, fontSize: 24, borderRadius: 16, animation: 'pulse 1.5s infinite' }}>
          K
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// ===== ROUTER PRINCIPAL =====
function AppRouter() {
  const { isAuthenticated, loading } = useFinance();

  if (loading) {
    return (
      <div className="login-container">
        <div className="sidebar-brand-logo" style={{ width: 56, height: 56, fontSize: 24, borderRadius: 16, animation: 'pulse 1.5s infinite' }}>
          K
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <Register />} />

      <Route element={<ProtectedRoute><AuthLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="admin/dashboard" element={<SuperAdminDashboard />} />
        <Route path="admin/users" element={<SuperAdminUsers />} />
        <Route path="wallets" element={<Wallets />} />
        <Route path="wallets/:id" element={<WalletDetail />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="transactions/new" element={<TransactionForm />} />
        <Route path="transactions/:id/edit" element={<TransactionForm />} />
        <Route path="categories" element={<Categories />} />
        <Route path="budgets" element={<Budgets />} />
        <Route path="goals" element={<Goals />} />
        <Route path="goals/:id" element={<GoalDetail />} />
        <Route path="recurring" element={<RecurringTransactions />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ===== APP ROOT =====
export default function App() {
  return (
    <BrowserRouter basename={import.meta.env.VITE_APP_BASE_PATH || '/'}>
      <FinanceProvider>
        <ThemeProvider>
          <AppRouter />
        </ThemeProvider>
      </FinanceProvider>
    </BrowserRouter>
  );
}
